import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Button } from '@mui/material';
// components
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useCodeSystem } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useDialogStore } from 'src/stores/dialog';
import uuidv4 from 'src/utils/uuidv4';
import produce from 'immer';

// ----------------------------------------------------------------------

type FormValue = {
  name: string;
};

type PlanFormProps = {
  isEdit?: boolean;
  currentActivity?: string;
};

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
});

const resolver = yupResolver(schema);

export default function NewActivityTypeForm({
  isEdit,
  currentActivity: currentActivityType,
}: PlanFormProps) {
  const navigate = useNavigate();
  const confirm = useDialogStore((s) => s.confirmDialog);
  const { enqueueSnackbar } = useSnackbar();
  const [activityTypesCodeSystem, { update, isLoading }] = useCodeSystem('ccm-activity-types', {
    autofetch: isEdit,
  });
  const activityType = useMemo(
    () => activityTypesCodeSystem?.concept?.find((concept) => concept.code === currentActivityType),
    [activityTypesCodeSystem, currentActivityType]
  );

  const defaultValues = useMemo(
    () =>
      ({
        name: activityType?.display || '',
      } as FormValue),
    [activityType]
  );

  const methods = useForm({ resolver, defaultValues });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isEdit && currentActivityType) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, activityType]);

  const onSubmit = async (data: FormValue) => {
    if (isLoading) return;

    if (!activityTypesCodeSystem) {
      await update({
        id: 'ccm-activity-types',
        resourceType: 'CodeSystem',
        content: 'complete',
        description: 'Activity List Automation Form - Activity Types',
        status: 'active',
        concept: [
          {
            code: uuidv4(),
            display: data.name,
          },
        ],
      });
      navigate('..');
      return;
    }

    try {
      if (isEdit && currentActivityType) {
        await update({
          ...activityTypesCodeSystem,
          concept:
            activityTypesCodeSystem.concept?.map((concept) => {
              if (concept.code === currentActivityType) {
                return {
                  ...concept,
                  display: data.name,
                };
              }

              return concept;
            }) || [],
          ...data,
        });
      } else {
        activityTypesCodeSystem?.concept?.push({
          code: uuidv4(),
          display: data.name,
        });

        await update(activityTypesCodeSystem);
      }

      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      navigate(isEdit ? '../..' : '..');
    } catch (error) {
      console.error(error);
    }
  };

  const onDelete = async () => {
    if (!activityTypesCodeSystem) return;

    // ask for confirmation before deleting
    const confirmed = await confirm({
      title: 'Delete Activity Type',
      description: 'Are you sure you want to delete this activity type?',
    });

    if (confirmed) {
      try {
        await update(
          produce(activityTypesCodeSystem, (draft) => {
            const concept = draft.concept?.find((c) => c.code === currentActivityType);

            if (!concept) {
              return;
            }

            const property = concept.property?.find((p) => p.code === 'active');

            if (property) {
              property.valueBoolean = false;
            } else {
              concept.property ??= [];
              concept.property.push({
                code: 'active',
                valueBoolean: false,
              });
            }
          })
        );

        enqueueSnackbar('Delete success!');
        navigate('..');
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 3 }}>
        <Stack
          gap={3}
          sx={{
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
        >
          <RHFTextField name="name" label="Activity Type Name" />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }} gap={2}>
          {isEdit && (
            <Button variant="text" color="error" onClick={onDelete}>
              Delete
            </Button>
          )}
          <Button variant="text" color="inherit" component={RouterLink} to="..">
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {!isEdit ? 'Create Activity Type' : 'Save Changes'}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  );
}
